import React from "react";
import { TsFileUploader } from "../../components/TsFileUploader/TsFileUploader";
import { TsJobProgressModal } from "../../components/modals/TsJobProgressModal/TsJobProgressModal";
import {
  Row,
  Col,
  TextBox,
  ConfirmationModal,
  Button,
  DataTable,
  DataTableColumn,
  Panel,
  DownloadButton,
  NewTabLink,
} from "@countysuite/ui";
import {
  getImportRowsApi,
  beginFileImportApi,
  beginCaseCreationApi,
  removeJobFromClaimApi,
  undoTaxClaimApi,
  saveTaxClaimDetailsApi,
} from "./api/apiCalls";
import { TaxClaimImportRow, Defendant } from "./TaxClaimTypes";
import "./TaxClaimEditPage.scss";
import { threadId } from "worker_threads";

type State = {
  taxClaimId: number;
  rows: TaxClaimImportRow[];
  jobKey: string;
  description: string;
  notes?: string;
  currentModal: string;
  saveButtonPressed: boolean;
  uploadOverwriteConfirmed?: boolean;
};

export type Props = {
  initialTaxClaimId: number;
  initialJobKey?: string;
  initialDescription: string;
  initialNotes?: string;

  caseLinkUrl: string;
  contactLinkUrl: string;
  closeUrl: string;
  exportToCsvUrl: string;
};

const modalNames = {
  CONFIRM_CREATE: "Confirm Case Creation",
  CONFIRM_UNDO: "Confirm Undo Data Import",
  CONFIRM_OVERWRITE: "Confirm Upload Overwrite",
  BACKGROUND_JOB: "Data Import Process",
};

export class TaxClaimEditPage extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      saveButtonPressed: false,
      taxClaimId: props.initialTaxClaimId || 0,
      jobKey: props.initialJobKey || "",
      description: props.initialDescription || "",
      notes: props.initialNotes || "",
      rows: [],
      currentModal: "",
      uploadOverwriteConfirmed: false,
    };
  }

  fetchImportRows = async (): Promise<void> => {
    if (!this.state.taxClaimId) return;

    const res = await getImportRowsApi(this.state.taxClaimId);

    this.setState({ rows: res.rows });
  };

  saveDetails = async (): Promise<void> => {
    if (!this.state.taxClaimId) return;

    await saveTaxClaimDetailsApi(
      this.state.taxClaimId,
      this.state.description,
      this.state.notes || ""
    );
  };

  beginFileImport = async (finishedFile: string): Promise<void> => {
    //overwrite the upload overwrite confirmed for the next upload session
    this.setState({ uploadOverwriteConfirmed: false });
    const fileImportApiData = {
      taxClaimId: this.state.taxClaimId,
      tempFileName: finishedFile,
      description: this.state.description,
      notes: this.state.notes,
    };
    const res = await beginFileImportApi(fileImportApiData);

    if (res.success) {
      this.setState({ jobKey: res.jobKey });
    } else {
      this.setState({ jobKey: "" });
    }
  };

  beginCaseCreation = async () => {
    const apiResponse = await beginCaseCreationApi(this.state.taxClaimId);
    if (apiResponse.success) {
      this.setState({ jobKey: apiResponse.jobKey });
    } else {
      //todo: better visible error display
      alert(apiResponse.message || "An internal server error has occurred");
    }
  };

  jobCancelled = async () => {
    await removeJobFromClaimApi(this.state.taxClaimId, this.state.jobKey || "");
    await this.fetchImportRows();
    this.setState({ jobKey: "" });
  };

  jobDone = () => {
    if (this.state.taxClaimId > 0) {
      this.fetchImportRows();
    }
  };

  undoImport = async () => {
    if (!this.state.taxClaimId) return;
    const res = await undoTaxClaimApi(this.state.taxClaimId);
    if (res.success) {
      this.setState({ jobKey: "", rows: [] });
    }
  };

  shouldPromptForUploadOverwrite = (fileName: string) => {
    if (this.state.rows && this.state.rows.length > 0) {
      this.showModal(modalNames.CONFIRM_OVERWRITE);
    } else {
      this.confirmUpload();
    }
  };

  showModal = (modalName: string) => this.setState({ currentModal: modalName });

  hideModal = () => this.setState({ currentModal: "" });

  closeJobModal = (percent: number) => {
    if (percent >= 100) {
      this.fetchImportRows();
      this.setState({ jobKey: "", currentModal: "" });
    } else {
      window.location.href = this.props.closeUrl;
    }
  };

  confirmCreateCase = () => {
    this.hideModal();
    this.beginCaseCreation();
  };

  confirmUndoCases = () => {
    this.hideModal();
    this.undoImport();
  };

  confirmUpload = () => {
    this.hideModal();
    //this feeds into the TsFileUploader as a prop and tells it to start the Upload on next update
    this.setState({ uploadOverwriteConfirmed: true });
  };

  componentDidMount = (): void => {
    this.fetchImportRows();
  };

  handleTextBox = (e: any) => {
    //use the name on the Input to determine which state property to update automatically
    //better than writing a bunch of callbacks for every textbox
    const key: keyof State = e.target.name;
    const value: any = e.target.value;
    this.setState((prevState) => ({ ...prevState, [key]: value }));
  };
  dowloadCsv = (): void => {
    const csvDownloadLink = `${this.props.exportToCsvUrl}?taxClaimId=${this.state.taxClaimId}`;
    window.location.href = csvDownloadLink;
  };

  getTableColumnDefinitions = (): DataTableColumn<TaxClaimImportRow>[] => {
    const columns: DataTableColumn<TaxClaimImportRow>[] = [
      {
        Header: "Parcel ID",
        accessor: "externalSystemID",
      },
      {
        Header: "Case Number",
        id: "caseNumber",
        width: 130,
        accessor: "caseNumber",
        Cell: (props) => {
          const row: TaxClaimImportRow = props.original;
          if (row.caseID && row.caseNumber) {
            let url = `${this.props.caseLinkUrl}?caseId=${row.caseID}`;
            return <NewTabLink href={url}>{row.caseNumber}</NewTabLink>;
          } else {
            return "Not Imported";
          }
        },
      },
      {
        Header: "Owner",
        id: "ownerCol",
        width: 350,
        filterable: false,
        Cell: (props) => {
          const row: TaxClaimImportRow = props.original;
          let importedOwner:
            | string
            | JSX.Element = `${row.owner1} ${row.owner2}`.trim();
          if (row.caseID && row.caseNumber) {
            let url = `${this.props.contactLinkUrl}?caseId=${row.caseID}`;
            importedOwner = <NewTabLink href={url}>{importedOwner}</NewTabLink>;
          }

          function getName(def: Defendant): string {
            if (def.isCompany) {
              return def.nameLast;
            } else {
              //use regex to transform duplicate spaces to 1 space
              return `${def.nameLast}, ${def.nameFirst || ""} ${def.nameMiddle || ""} ${def.nameSuffix || ""}`
                .replace(/  +/g, " ")
                .trim();
            }
          }

          return (
            <div>
              <span>{importedOwner}</span>
              <ol className="contact-preview">
                {row.defendants.map((def) => (
                  <li>{getName(def)}</li>
                ))}
              </ol>
            </div>
          );
        },
      },
      {
        Header: "Address",
        id: "addressCol",
        width: 250,
        filterable: false,
        Cell: (props) => {
          const row: TaxClaimImportRow = props.original;
          return (
            <div>
              {row.ownerAddress1 || row.ownerAddress2 || row.ownerAddress3}
            </div>
          );
        },
      },
      {
        Header: "Total Amount",
        id: "totalCol",
        accessor: "totalTotal",
        width: 100,
        className: "dt-body-right",
        Cell: (props) => {
          return <div className="align-right">${props.value}</div>;
        },
      },
    ];

    return columns;
  };

  render() {
    const columnDefs = this.getTableColumnDefinitions();

    //shouldnt be able to upload while theres a job running or theres no rows
    const hasJobKey: boolean = this.state.jobKey.length > 0;
    const jobIsRunningOrTheresNoRows: boolean =
      this.state.rows.length <= 0 || hasJobKey;
    const noCasesHaveBeenImported: boolean =
      this.state.rows.some((row) => !!row.caseID) === false;

    const currentModal = hasJobKey
      ? modalNames.BACKGROUND_JOB
      : this.state.currentModal;

    const saveBtnCss = this.state.saveButtonPressed ? "btn-pressed" : "";

    return (
      <div id="TaxClaimEditPage" className="container-fluid">
        <TsJobProgressModal
          key="job-progress-modal"
          show={currentModal === modalNames.BACKGROUND_JOB}
          onClose={this.closeJobModal}
          jobKey={this.state.jobKey}
          onJobCancel={this.jobCancelled}
          onJobDone={this.jobDone}
        ></TsJobProgressModal>

        <ConfirmationModal
          key="confirm-case-creation-modal"
          show={currentModal === modalNames.CONFIRM_CREATE}
          onHide={this.hideModal}
          title="Case Creation"
          onConfirm={this.confirmCreateCase}
          onCancel={this.hideModal}
        >
          Are you sure you want to create{" "}
          {this.state.rows.filter((r) => !r.caseID).length} cases?
        </ConfirmationModal>

        <ConfirmationModal
          key="confirm-undo-modal"
          show={currentModal === modalNames.CONFIRM_UNDO}
          onHide={this.hideModal}
          title="Undo Import"
          onConfirm={this.confirmUndoCases}
          onCancel={this.hideModal}
        >
          Are you sure you want to undo this tax claim import? This will delete
          ALL imported cases, including any additional data that might have been
          entered manually.
        </ConfirmationModal>

        <ConfirmationModal
          key="confirm-upload-overwrite-modal"
          show={currentModal === modalNames.CONFIRM_OVERWRITE}
          onHide={this.hideModal}
          title="Upload Overwrite Confirmation"
          onConfirm={this.confirmUpload}
          onCancel={this.hideModal}
        >
          Uploading a new file will DELETE all associated import data from the
          system, including cases and defendants. Are you SURE you wish to
          proceed?
        </ConfirmationModal>

        <Panel
          title="Edit Tax Claim Details"
          buttons={() => (
            <>
              <Button
                onClick={this.saveDetails}
                className={saveBtnCss}
                showFeedback
              >
                Save
              </Button>
              <TsFileUploader
                accept="text/plain"
                disableUpload={!this.state.uploadOverwriteConfirmed}
                onFileSelected={this.shouldPromptForUploadOverwrite}
                onUploadComplete={this.beginFileImport}
                uploadUrl="TaxClaim/UploadFileChunk"
                hideProgressBar={true}
                disableButton={hasJobKey || !this.state.description}
              />
            </>
          )}
        >
          <Row>
            <Col xs={6}>
              <TextBox
                label="Description"
                name="description"
                value={this.state.description}
                onChange={this.handleTextBox}
              />
            </Col>
          </Row>
          <Row>
            <Col xs={6}>
              <TextBox
                label="Notes"
                name="notes"
                multiline
                rows={5}
                value={this.state.notes}
                onChange={this.handleTextBox}
              />
            </Col>
          </Row>
        </Panel>

        <Panel
          title="Import File Results"
          buttons={() => (
            <>
              <Button
                onClick={() => this.dowloadCsv()}
                disabled={jobIsRunningOrTheresNoRows || noCasesHaveBeenImported}
              >
                Export To CSV
              </Button>
              <Button
                onClick={() => this.showModal(modalNames.CONFIRM_UNDO)}
                disabled={jobIsRunningOrTheresNoRows}
                color="danger"
              >
                Undo
              </Button>
              <Button
                onClick={() => this.showModal(modalNames.CONFIRM_CREATE)}
                disabled={jobIsRunningOrTheresNoRows}
              >
                Create Cases
              </Button>
            </>
          )}
        >
          <DataTable data={this.state.rows} columns={columnDefs} />
        </Panel>
      </div>
    );
  }
}
